import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import RestaurantCalendarOpenClosedDays from "../components/restaurant_schedule/RestaurantCalendarOpenClosedDays";
import EstimatedTimePage from "../components/EstimatedTimeCustomersSpend";
import DropDownButton from "../components/DropDownButton";
import RestaurantNotifications from "../components/notifications/RestaurantNotifications";
import InputDayOrHour from "../components/InputDayOrHour";
import { CustomModal } from "../components/CustomModal";
import { fetchRestaurantOpenDays, updateRestaurant, fetchHoursEach, fetchPlaceOfTable } from "../actions/restaurantActions";
import { Container, CardHeader, CardHeaderTitle, ModernButton, GridContainer, SettingsCard, SectionTitle, Text, TabsContainer } from "../components/DefaultImports";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faLock, faUtensils, faUsers } from "@fortawesome/free-solid-svg-icons";
function Notifications() {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [restaurant_id, setRestaurant_id] = useState(false);
  const [eachTime, setEachTime] = useState("00:15");
  const [acceptPeoplePerEachTime, setAcceptPeoplePerEachTime] = useState("");
  const [limitPeopleReservation, setLimitPeopleReservation] = useState("");
  const [errorMessage, setErrorMessage] = useState("ok_status");
  const [showError, setShowError] = useState(false);
  const [activeTab, setActiveTab] = useState("restaurant_notifications");
  const [reconfirmation_duration, setReconfirmation_duration] = useState("");
  const [reconfirmation_time_before_date, setReconfirmation_time_before_date] = useState("");
  const [messageReminderTime, setMessageReminderTime] = useState("");
  const [timeToCancelledWarning, setTimeToCancelledWarning] = useState("");
  const [reconfirmationCompareError, setReconfirmationCompareError] = useState("");
  const [didTrySave, setDidTrySave] = useState(false);
  const [touchedBeforeDate, setTouchedBeforeDate] = useState(false);
  const [showReminderUpgradeModal, setShowReminderUpgradeModal] = useState(false);
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurantDetails;
  const isReminderAndReconfirmationBlocked = !restaurants || [0, 1, 2, 4, 5].includes(Number(restaurants?.sub_lvl));
  useEffect(() => {
    if (restaurants) {
      setRestaurant_id(restaurants.id);
      dispatch(fetchPlaceOfTable(restaurants.id));
    }
  }, [restaurants, dispatch]);
  useEffect(() => {
    dispatch(fetchRestaurantOpenDays(restaurant_id));
    dispatch(fetchHoursEach(restaurant_id));
  }, [dispatch, restaurant_id]);
  useEffect(() => {
    if (restaurants) {
      if (restaurants.each_time) {
        setEachTime(restaurants.each_time);
      }
      if (restaurants.accept_people_per_each_time !== undefined) {
        setAcceptPeoplePerEachTime(restaurants.accept_people_per_each_time);
      }
      if (restaurants.limit_people_reservation !== undefined) {
        setLimitPeopleReservation(restaurants.limit_people_reservation);
      }
      setReconfirmation_duration(restaurants?.reconfirmation_duration != null ? String(restaurants.reconfirmation_duration) : "");
      setReconfirmation_time_before_date(restaurants?.reconfirmation_time_before_date != null ? String(restaurants.reconfirmation_time_before_date) : "");
      setMessageReminderTime(restaurants.send_message_reminder_to_customers);
      setTimeToCancelledWarning(restaurants.time_to_cancelled_warning);
    }
  }, [restaurants]);
  const toIntOrNull = value => {
    const cleanValue = String(value ?? "").trim();
    if (cleanValue === "") {
      return null;
    }
    const parsedValue = parseInt(cleanValue, 10);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };
  useEffect(() => {
    const durationNumber = toIntOrNull(reconfirmation_duration);
    const beforeNumber = toIntOrNull(reconfirmation_time_before_date);
    if (durationNumber != null && beforeNumber != null && beforeNumber > durationNumber) {
      setReconfirmationCompareError(t("ui.i18nMessages.reconfirmationRangeError"));
    } else {
      setReconfirmationCompareError("");
    }
  }, [reconfirmation_duration, reconfirmation_time_before_date]);
  const handleReconfirmation = value => {
    if (isReminderAndReconfirmationBlocked) {
      return;
    }
    setReconfirmation_duration(value);
  };
  const handleReconfirmationTimeBeforeDate = value => {
    if (isReminderAndReconfirmationBlocked) {
      return;
    }
    setTouchedBeforeDate(true);
    setReconfirmation_time_before_date(value);
  };
  const handleMessageReminderTime = value => {
    if (isReminderAndReconfirmationBlocked) {
      return;
    }
    setMessageReminderTime(value);
  };
  const handleTimeCancelWarning = value => {
    setTimeToCancelledWarning(value);
  };
  function checkEmptyStates() {
    const emptyStates = [];
    if (!eachTime) {
      emptyStates.push(t("ui.i18nFinalCleanup.timeInterval"));
    }
    if (!acceptPeoplePerEachTime || acceptPeoplePerEachTime <= 0) {
      emptyStates.push(t("ui.i18nFinalCleanup.capacityPerSlot"));
    }
    if (!limitPeopleReservation || limitPeopleReservation <= 0) {
      emptyStates.push(t("ui.i18nFinalCleanup.groupLimit"));
    }
    return emptyStates.length > 0 ? t("ui.i18nFinalCleanup.fillFields", { fields: emptyStates.join(", ") }) : "ok_status";
  }
  function checkForErrorsAndUpdateRestaurant() {
    const statusError = checkEmptyStates();
    setErrorMessage(statusError);
    if (statusError === "ok_status") {
      setShowError(false);
      dispatch(updateRestaurant(restaurant_id, {
        time_to_cancelled_warning: timeToCancelledWarning,
        send_message_reminder_to_customers: isReminderAndReconfirmationBlocked ? restaurants?.send_message_reminder_to_customers : messageReminderTime,
        reconfirmation_time_before_date: isReminderAndReconfirmationBlocked ? restaurants?.reconfirmation_time_before_date : reconfirmation_time_before_date,
        reconfirmation_duration: isReminderAndReconfirmationBlocked ? restaurants?.reconfirmation_duration : reconfirmation_duration
      }));
      toast.success(t("ui.i18nMessages.savedSuccessfully"), {
        style: {
          color: "#EE754C"
        },
        icon: <FontAwesomeIcon icon={faCheckCircle} style={{
          color: "#EE754C"
        }} />,
        progressStyle: {
          backgroundColor: "#EE754C"
        },
        autoClose: 1000
      });
    } else {
      setShowError(true);
    }
  }
  const handleSubmit = event => {
    event.preventDefault();
    setDidTrySave(true);
    checkForErrorsAndUpdateRestaurant();
  };
  return <Container>
      <ToastContainer />

      <CardHeader>
        <CardHeaderTitle>{t("ui.screens.notifications.notificacoes")}</CardHeaderTitle>
      </CardHeader>

      <TabsContainer data-setup-tour="notifications">
        <StyledTabButton type="button" active={activeTab === "restaurant_notifications"} onClick={() => setActiveTab("restaurant_notifications")}>
          <FontAwesomeIcon icon={faUtensils} />{t("ui.screens.notifications.restaurante")}</StyledTabButton>

        <StyledTabButton type="button" active={activeTab === "client_notifications"} onClick={() => setActiveTab("client_notifications")}>
          <FontAwesomeIcon icon={faUsers} />{t("ui.screens.notifications.clientes")}</StyledTabButton>
      </TabsContainer>

      {activeTab === "restaurant_notifications" && <RestaurantNotifications restaurant_id={restaurant_id} />}

      {activeTab === "client_notifications" && <Form onSubmit={handleSubmit}>
          <GridContainer style={{
        marginTop: "10px"
      }}>
            {/*
             <SettingsCard>
              <SectionTitle>Reconfirmações</SectionTitle>
                <Text>
                <FieldRow>
                  <span>
                    Enviar pedido de reconfirmação se a reserva
                    for marcada com mais de:
                  </span>
                    <SmallNumberInput
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={reconfirmation_duration}
                    onChange={(event) =>
                      handleReconfirmation(event.target.value)
                    }
                    placeholder="0"
                  />
                    <span>dias de antecedência.</span>
                </FieldRow>
              </Text>
                <Text>
                <FieldRow>
                  <span>
                    Enviar pedido de reconfirmação ao cliente:
                  </span>
                    <SmallNumberInput
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={reconfirmation_time_before_date}
                    onChange={(event) =>
                      handleReconfirmationTimeBeforeDate(
                        event.target.value,
                      )
                    }
                    placeholder="0"
                  />
                    <span>dias antes da reserva.</span>
                </FieldRow>
                  {(didTrySave || touchedBeforeDate) &&
                  reconfirmationCompareError && (
                    <InlineError>
                      {reconfirmationCompareError}
                    </InlineError>
                  )}
              </Text>
             </SettingsCard>
             */}

            <SettingsCard>
              <SectionTitle>{t("ui.screens.notifications.lembretes")}</SectionTitle>

              <Text>
                <FieldRow>
                  <span>{t("ui.screens.notifications.enviarLembreteDaReservaAoCliente")}</span>

                  <BlockedFieldWrapper $blocked={isReminderAndReconfirmationBlocked}>
                    <InputDayOrHour initialValue={restaurants?.send_message_reminder_to_customers} setValue={handleMessageReminderTime} displayEmpty={isReminderAndReconfirmationBlocked} />

                    {isReminderAndReconfirmationBlocked && <BlockedFieldOverlay data-blocked-field-overlay="true" role="button" tabIndex={0} aria-label={t("ui.screens.notifications.verPlanosParaLembretes")} onClick={() => setShowReminderUpgradeModal(true)} onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setShowReminderUpgradeModal(true);
                  }
                }}>
                        <FontAwesomeIcon icon={faLock} />
                      </BlockedFieldOverlay>}
                  </BlockedFieldWrapper>

                  <span>{t("ui.screens.notifications.antesDaHoraDaReserva")}</span>
                </FieldRow>
              </Text>
            </SettingsCard>

            <SettingsCard>
              <SectionTitle>{t("ui.screens.notifications.notificacoes2")}</SectionTitle>

              <Text>
                <FieldRow>
                  <span>{t("ui.screens.notifications.receberNotificacaoSeOClienteCancelar")}</span>

                  <InputDayOrHour initialValue={restaurants?.time_to_cancelled_warning} setValue={handleTimeCancelWarning} />

                  <span>{t("ui.screens.notifications.deAntecedencia")}</span>
                </FieldRow>
              </Text>
            </SettingsCard>
          </GridContainer>

          <ButtonsContainer>
            <ModernButton type="submit" color="#EE754C" fontSize="14px">{t("ui.screens.notifications.guardar")}</ModernButton>
          </ButtonsContainer>
        </Form>}

      <CustomModal show={showReminderUpgradeModal} onHide={() => setShowReminderUpgradeModal(false)} title={t("ui.screens.notifications.lembretesEReconfirmacoes")} body={<UpgradeModalText>{t("ui.screens.notifications.osLembretesEPedidosDeReconfirmacao")}{" "}
            <UpgradeModalHighlight>{t("ui.screens.notifications.completo")}</UpgradeModalHighlight>.
          </UpgradeModalText>} footer={<>
            <ModernButton type="button" color="#99b0ad" onClick={() => setShowReminderUpgradeModal(false)}>{t("ui.screens.notifications.agoraNao")}</ModernButton>

            <ModernButton type="button" color="#EE754C" onClick={() => {
        setShowReminderUpgradeModal(false);
        navigate(`/pick_plan_and_pay/${restaurants?.id}`);
      }}>{t("ui.screens.notifications.verPlanos")}</ModernButton>
          </>} />
    </Container>;
}
export default Notifications;
const Form = styled.form`
  width: 100%;
`;
const StyledTabButton = styled.button`
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 7px;
  background-color: ${props => props.active ? "#EE754C" : "#f2f2f2"};
  color: ${props => props.active ? "white" : "#333"};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${props => props.active ? "#d96945" : "#e6e6e6"};
  }

  @media (max-width: 768px) {
    font-size: 14px;
    flex: 1 1 auto;
    justify-content: center;
  }
`;
const FieldRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #6c757d;
`;
const BlockedFieldWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  border-radius: 6px;

  ${({
  $blocked
}) => $blocked ? `
        background: rgba(238, 117, 76, 0.08);
        box-shadow: 0 0 0 2px rgba(238, 117, 76, 0.22);

        input,
        select,
        textarea {
          border-color: #EE754C !important;
          background-color: #fff7f4 !important;
          box-shadow: none !important;
        }

        > *:not([data-blocked-field-overlay="true"]) {
          pointer-events: none;
        }
      ` : ""}
`;
const BlockedFieldOverlay = styled.span`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 140px;
  color: #ee754c;
  cursor: pointer;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.01);

  svg {
    filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.85));
  }
`;
const UpgradeModalText = styled.p`
  margin: 0;
`;
const UpgradeModalHighlight = styled.span`
  color: #ee754c;
  font-weight: 700;
`;
const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
const SmallNumberInput = styled.input`
  width: 90px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: #fafafa;
  outline: none;

  &:focus {
    border-color: #ee754c;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.18);
  }
`;
const InlineError = styled.div`
  margin-top: 6px;
  color: #dc3545;
  font-size: 12px;
`;
